import type { ReactNode } from 'react'
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import SearchTravellersDropdown from 'components/Search/SearchForm/SearchTravellersInput/SearchTravellersDropdown'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import TravellersFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/TravellersFloatingPanel'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import { getHotelSearchDateSummary } from 'components/Search/utils'
import { getHotelSearchDateState } from './ListingDrawerSearchDates'
import TripContextSearchDateDropdown from './TripContextSearchDateDropdown'
import type { TripStop } from 'tripPlanner/api/types'
import type { CalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'
import { useListingDrawerHotelDateGuests, type ListingDrawerHotelDateGuestsParams } from '../hooks/useListingDrawerHotelSearchDraft'

const FiltersRow = styled.div`
  position: sticky;
  top: 0;
  z-index: ${zIndex.menuInModal};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(8)};
  padding-block: ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-bottom: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

export interface ListingDrawerHotelDateGuestsHandle {
  openDates: () => void
}

interface Props extends ListingDrawerHotelDateGuestsParams {
  showChips?: boolean
  focusMonth?: CalendarFocusMonth
  tripStops?: ReadonlyArray<TripStop>
  contextStopId?: number
  children?: ReactNode
}

const ListingDrawerHotelDateGuestsChips = forwardRef<ListingDrawerHotelDateGuestsHandle, Props>(function ListingDrawerHotelDateGuestsChips(props, ref) {
  const { dates, rooms, compact, showChips = true, maxGuests, focusMonth, tripStops, contextStopId, children } = props
  const { rooms: pendingRooms, dateOpen, guestsOpen, hasGuestsError, openDates, openGuests, closeDates, closeGuests, confirmDates, confirmGuests, handleRoomChange, selectAnytime, dateDropdownProps } = useListingDrawerHotelDateGuests(props)
  const intl = useIntl()
  const anchorRef = useRef<HTMLDivElement>(null)
  const dateRef = useRef<HTMLButtonElement>(null)
  const guestsRef = useRef<HTMLButtonElement>(null)
  useImperativeHandle(ref, () => ({ openDates }), [openDates])
  const dateSearchProps = useDateSearchProps({ onAnytimeDatesSelected: selectAnytime })
  const dateLabel = useMemo(() => getHotelSearchDateSummary(intl, getHotelSearchDateState(dates)), [dates, intl])
  const applyLabel = intl.formatMessage({
    id: 'ENxPPE_4gjNvtXZpYAUq',
    defaultMessage: 'Apply',
    description: 'Apply button text for search form panels in the search interface',
  })
  const guestsIcon = useMemo(() => <DropdownChevron open={guestsOpen} />, [guestsOpen])
  const datesIcon = useMemo(() => <DropdownChevron open={dateOpen} />, [dateOpen])
  return (
    <FiltersRow ref={anchorRef} data-testid="hotel-listing-filters">
      {showChips && (
        <>
          <AssistChip ref={guestsRef} variant="outline" size="medium" onClick={openGuests} error={hasGuestsError} endIcon={guestsIcon}>
            <FormattedOccupancy occupancies={rooms} />
          </AssistChip>
          <AssistChip ref={dateRef} variant="outline" size="medium" onClick={openDates} endIcon={datesIcon}>
            {dateLabel}
          </AssistChip>
        </>
      )}
      {compact && (
        <>
          <DateFloatingPanel {...dateSearchProps} isOpen={dateOpen} onClose={closeDates} onGoBack={closeDates} onConfirm={confirmDates} confirmText={applyLabel} focusMonth={focusMonth} />
          <TravellersFloatingPanel isOpen={guestsOpen} onClose={closeGuests} onGoBack={closeGuests} onConfirm={confirmGuests} confirmText={applyLabel} maxOccupants={maxGuests} />
        </>
      )}
      {!compact && (
        <>
          <TripContextSearchDateDropdown {...dateDropdownProps} triggerRef={dateRef} anchorRef={anchorRef} open={dateOpen} portaled focusMonth={focusMonth} tripStops={tripStops} contextStopId={contextStopId} />
          <SearchTravellersDropdown rooms={pendingRooms} onRoomChange={handleRoomChange} onRoomsApply={confirmGuests} triggerRef={guestsRef} anchorRef={anchorRef} open={guestsOpen} onClose={closeGuests} childVariant="child-age" portaled />
        </>
      )}
      {children}
    </FiltersRow>
  )
})
export default ListingDrawerHotelDateGuestsChips
